import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Refresh,
  DeleteSweep,
  PlayCircleOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  Cancel,
  RemoveCircleOutline,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  sectionTitle: {
    fontWeight: 600,
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  fieldChip: {
    marginRight: theme.spacing(0.5),
  },
  recognized: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #a5d6a7",
  },
  unhandled: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffcc80",
  },
  rawBox: {
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    fontFamily: "monospace",
    fontSize: "0.75rem",
    padding: theme.spacing(2),
    borderRadius: 6,
    overflowX: "auto",
    whiteSpace: "pre",
    margin: 0,
  },
  empty: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
  tableWrap: {
    overflowX: "auto",
  },
}));

const statusColor = {
  processed: { labelKey: "settings.instagramWebhooks.status.processed", color: "#2e7d32", bg: "#e8f5e9" },
  received: { labelKey: "settings.instagramWebhooks.status.received", color: "#1565c0", bg: "#e3f2fd" },
  ignored: { labelKey: "settings.instagramWebhooks.status.ignored", color: "#616161", bg: "#f5f5f5" },
  error: { labelKey: "settings.instagramWebhooks.status.error", color: "#c62828", bg: "#ffebee" },
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { hour12: false });
  } catch {
    return iso;
  }
};

const EventRow = ({ event, recognizedFields }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const st = statusColor[event.status] || statusColor.received;
  const recognized = recognizedFields.includes(event.field);

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{formatTime(event.receivedAt)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={event.field}
            className={`${classes.fieldChip} ${recognized ? classes.recognized : classes.unhandled}`}
          />
          {event.simulated && (
            <Chip size="small" variant="outlined" label={i18n.t("settings.instagramWebhooks.simulated")} />
          )}
        </TableCell>
        <TableCell>
          {recognized ? (
            <Tooltip title={i18n.t("settings.instagramWebhooks.recognizedTooltip")}>
              <Chip size="small" icon={<CheckCircle style={{ color: "#2e7d32" }} />} label={i18n.t("settings.instagramWebhooks.recognized")} className={classes.recognized} />
            </Tooltip>
          ) : (
            <Tooltip title={i18n.t("settings.instagramWebhooks.unhandledTooltip")}>
              <Chip size="small" icon={<RemoveCircleOutline style={{ color: "#e65100" }} />} label={i18n.t("settings.instagramWebhooks.unhandled")} className={classes.unhandled} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <span style={{ color: st.color, backgroundColor: st.bg, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 600 }}>
            {i18n.t(st.labelKey)}
          </span>
        </TableCell>
        <TableCell>
          {event.signatureValid === false ? (
            <Tooltip title={i18n.t("settings.instagramWebhooks.signatureInvalidTooltip")}><span style={{ color: "#c62828" }}><Cancel style={{ fontSize: 16, verticalAlign: "-0.2em", marginRight: 4 }} />{i18n.t("settings.instagramWebhooks.signatureInvalid")}</span></Tooltip>
          ) : event.signatureValid === true ? (
            <Tooltip title={i18n.t("settings.instagramWebhooks.signatureValidTooltip")}><span style={{ color: "#2e7d32" }}><CheckCircle style={{ fontSize: 16, verticalAlign: "-0.2em" }} /></span></Tooltip>
          ) : (
            <span style={{ color: "#9e9e9e" }}>—</span>
          )}
        </TableCell>
        <TableCell>{event.connectionName || <span style={{ color: "#9e9e9e" }}>{event.igUserId || "—"}</span>}</TableCell>
        <TableCell style={{ maxWidth: 280, fontSize: "0.8rem" }}>{event.detail}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box my={1}>
              <Typography variant="caption" color="textSecondary">{i18n.t("settings.instagramWebhooks.payload")}</Typography>
              <pre className={classes.rawBox}>{JSON.stringify(event.payload, null, 2)}</pre>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const InstagramWebhookMonitor = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [events, setEvents] = useState([]);
  const [knownFields, setKnownFields] = useState([]);
  const [recognizedFields, setRecognizedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/instagram/webhook-monitor");
      setEvents(data.events || []);
      setKnownFields(data.knownFields || []);
      setRecognizedFields(data.recognizedFields || []);
    } catch (err) {
      toast.error(i18n.t("settings.instagramWebhooks.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Atualização ao vivo via socket
  useEffect(() => {
    if (!user?.companyId) return undefined;
    const socket = socketManager.getSocket(user.companyId);
    const handler = (data) => {
      if (data?.action === "create" && data.event) {
        setEvents((prev) => [data.event, ...prev].slice(0, 200));
      }
    };
    socket.on(`company-${user.companyId}-instagram-webhook`, handler);
    return () => {
      socket.off(`company-${user.companyId}-instagram-webhook`, handler);
    };
  }, [socketManager, user]);

  const handleClear = async () => {
    try {
      await api.delete("/instagram/webhook-monitor");
      setEvents([]);
      toast.success(i18n.t("settings.instagramWebhooks.toasts.cleared"));
    } catch {
      toast.error(i18n.t("settings.instagramWebhooks.toasts.clearError"));
    }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await api.post("/instagram/webhook-monitor/simulate", {});
      toast.success(i18n.t("settings.instagramWebhooks.toasts.simulated"));
      // o socket entrega os eventos; refetch garante consistência
      await fetchEvents();
    } catch {
      toast.error(i18n.t("settings.instagramWebhooks.toasts.simulateError"));
    } finally {
      setSimulating(false);
    }
  };

  return (
    <Paper className={classes.paper} elevation={0}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("settings.instagramWebhooks.title")}
      </Typography>
      <Typography className={classes.infoText}>
        {i18n.t("settings.instagramWebhooks.description")}
      </Typography>

      <div className={classes.legend}>
        {knownFields.map((f) => {
          const recognized = recognizedFields.includes(f);
          return (
            <Chip
              key={f}
              size="small"
              label={f}
              className={recognized ? classes.recognized : classes.unhandled}
            />
          );
        })}
      </div>
      <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8 }}>
        <span style={{ color: "#2e7d32" }}>■</span> {i18n.t("settings.instagramWebhooks.legend.recognized")} &nbsp;·&nbsp;
        <span style={{ color: "#e65100" }}>■</span> {i18n.t("settings.instagramWebhooks.legend.unhandled")}
      </Typography>

      <div className={classes.toolbar}>
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={fetchEvents} disabled={loading}>
          {i18n.t("common.refresh")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={simulating ? <CircularProgress size={16} /> : <PlayCircleOutline />}
          onClick={handleSimulate}
          disabled={simulating}
        >
          {i18n.t("settings.instagramWebhooks.simulateAll")}
        </Button>
        <Button size="small" variant="outlined" startIcon={<DeleteSweep />} onClick={handleClear}>
          {i18n.t("common.clear")}
        </Button>
      </div>

      <div className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("settings.instagramWebhooks.table.receivedAt")}</TableCell>
              <TableCell>{i18n.t("settings.instagramWebhooks.table.field")}</TableCell>
              <TableCell>Backend</TableCell>
              <TableCell>{i18n.t("common.status")}</TableCell>
              <TableCell>{i18n.t("settings.instagramWebhooks.table.signature")}</TableCell>
              <TableCell>{i18n.t("settings.instagramWebhooks.table.connection")}</TableCell>
              <TableCell>{i18n.t("settings.instagramWebhooks.table.detail")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={classes.empty}>
                    {loading ? i18n.t("common.loading") : i18n.t("settings.instagramWebhooks.empty")}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <EventRow key={event.id} event={event} recognizedFields={recognizedFields} />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
};

export default InstagramWebhookMonitor;
